import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CourseProvider, useCourses } from './context/CourseContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { Home } from './pages/Home';

// Lazy load page components to improve initial page speed and reduce unused JS
const Courses = lazy(() => import('./pages/Courses').then(m => ({ default: m.Courses })));
const CourseDetail = lazy(() => import('./pages/CourseDetail').then(m => ({ default: m.CourseDetail })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const LiveClasses = lazy(() => import('./pages/LiveClasses').then(m => ({ default: m.LiveClasses })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const SubjectVideos = lazy(() => import('./pages/SubjectVideos').then(m => ({ default: m.SubjectVideos })));
const CourseContent = lazy(() => import('./pages/CourseContent').then(m => ({ default: m.CourseContent })));
const LessonView = lazy(() => import('./pages/LessonView').then(m => ({ default: m.LessonView })));
const FreeClasses = lazy(() => import('./pages/FreeClasses'));
const PageViewer = lazy(() => import('./pages/PageViewer').then(m => ({ default: m.PageViewer })));
const BlogsList = lazy(() => import('./pages/BlogsList').then(m => ({ default: m.BlogsList })));
const BlogViewer = lazy(() => import('./pages/BlogViewer').then(m => ({ default: m.BlogViewer })));
const TeacherDashboard = lazy(() => import('./pages/TeacherDashboard').then(m => ({ default: m.TeacherDashboard })));

import { ErrorBoundary } from './components/ErrorBoundary';
import { ProfileGuard } from './components/ProfileGuard';
import { db, auth } from './firebase';
import { collection, addDoc, query, where, onSnapshot } from 'firebase/firestore';
import { ShieldAlert, AlertTriangle } from 'lucide-react';

function PageTracker() {
  const location = useLocation();
  const { homepageSettings } = useCourses();

  useEffect(() => {
    let sessionId = sessionStorage.getItem('educore_session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
      sessionStorage.setItem('educore_session_id', sessionId);
    }

    const logPageView = async () => {
      try {
        const payload = {
          path: location.pathname,
          userId: auth.currentUser?.uid || 'anonymous',
          email: auth.currentUser?.email || null,
          sessionId: sessionId,
          timestamp: new Date().toISOString(),
          date: new Date().toISOString().split('T')[0]
        };

        const colRef = collection(db, 'telemetry_views');
        await addDoc(colRef, payload);
      } catch (err) {
        console.error('Failed to log telemetry view:', err);
      }
    };

    logPageView();

    // Dynamically trigger Google Analytics PageView if available
    if (homepageSettings && homepageSettings.googleAnalyticsId) {
      const gaId = homepageSettings.googleAnalyticsId.trim();
      if (!gaId.includes('<script') && typeof (window as any).gtag === 'function') {
        (window as any).gtag('config', gaId, {
          page_path: location.pathname,
          page_title: document.title,
          page_location: window.location.href
        });
      }
    }
  }, [location.pathname, homepageSettings]);

  return null;
}

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('[Security] Error loading security events:', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

function SecurityInterceptor({ children }: { children: React.ReactNode }) {
  const [incident, setIncident] = useState<{
    id: string;
    type: 'terminate' | 'block' | 'warn';
    message?: string;
  } | null>(null);

  useEffect(() => {
    let sessionId = sessionStorage.getItem('educore_session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
      sessionStorage.setItem('educore_session_id', sessionId);
    }

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      const currentUid = user?.uid || 'anonymous';
      const targets = [sessionId, currentUid].filter(Boolean);

      const q = query(
        collection(db, 'security_actions'),
        where('targetId', 'in', targets)
      );

      const unsubscribeSnapshot = onSnapshot(q, (snapshot) => {
        let latestIncident: any = null;
        let latestTime = 0;

        snapshot.forEach((doc) => {
          const data = doc.data();
          const timestamp = new Date(data.timestamp || 0).getTime();
          const isFresh = (data.action === 'block') || (Date.now() - timestamp < 3 * 3600 * 1000); 
          if (isFresh && timestamp > latestTime) {
            latestTime = timestamp;
            latestIncident = {
              id: doc.id,
              type: data.action,
              message: data.message
            };
          }
        });

        if (latestIncident) {
          setIncident(latestIncident);
          if (latestIncident.type === 'terminate' || latestIncident.type === 'block') {
            auth.signOut().catch(() => {});
          }
        }
      }, (error) => {
        console.warn('[SecurityInterceptor] Security actions sync passive warning:', error);
      });

      return () => unsubscribeSnapshot();
    });

    return () => unsubscribeAuth();
  }, []);

  if (incident && (incident.type === 'terminate' || incident.type === 'block')) {
    return (
      <div className="fixed inset-0 bg-zinc-950 z-[999999] flex items-center justify-center p-4 font-mono select-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.08),transparent_80%)]" />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-650 via-rose-505 to-red-650 shadow-[0_2px_20px_rgba(239,68,68,0.5)]" />
        
        <div className="w-full max-w-xl bg-zinc-900/95 border border-red-500/20 rounded-[2.5rem] p-8 shadow-2xl relative backdrop-blur-md">
          <div className="flex items-center gap-4 border-b border-red-500/10 pb-5 mb-6">
            <div className="p-3 bg-red-500/10 rounded-2xl border border-red-500/20 text-red-500 animate-pulse">
              <ShieldAlert size={28} />
            </div>
            <div>
              <h1 className="text-sm font-black text-rose-500 tracking-widest uppercase">SECURITY PANEL LOCKDOWN</h1>
              <p className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wider">Access blocked by Administrative System Command</p>
            </div>
          </div>

          <div className="space-y-4 text-xs text-zinc-400">
            <div className="p-4 bg-zinc-950 rounded-2xl border border-zinc-800 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-zinc-550 uppercase text-[9px]">Command:</span>
                <span className="text-red-450 font-black uppercase tracking-wider">{incident.type === 'block' ? 'ADMIN_ACCOUNT_BAN' : 'TERMINATE_ACTIVE_SESSION'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-550 uppercase text-[9px]">Target Ref:</span>
                <span className="text-zinc-500 font-mono text-[9px]">{incident.id}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-550 uppercase text-[9px]">Status:</span>
                <span className="text-red-500 font-bold uppercase text-[9px] bg-red-500/10 px-2 py-0.5 rounded">EXCLUDED [403]</span>
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-zinc-500 text-[10px] uppercase font-bold">SYSTEM MESSAGE & REASON:</span>
              <p className="bg-red-950/20 p-4 border border-red-950/40 rounded-2xl text-rose-200 text-xs italic">
                "{incident.message || 'Access to this workspace session has been revoked by an administrator.'}"
              </p>
            </div>

            <div className="pt-4 border-t border-zinc-800 flex justify-between text-[9px] text-zinc-650">
              <span className="uppercase">EDUCORE CYBER INTELLIGENCE SYSTEM</span>
              <span className="animate-pulse text-red-500 font-bold">● EXCLUSION TRIGGERED</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      
      {incident && incident.type === 'warn' && (
        <div className="fixed bottom-6 right-6 z-[99999] max-w-sm bg-zinc-900 border-2 border-amber-500/40 rounded-[2rem] p-5 shadow-2xl shadow-zinc-950/60 font-mono animate-in slide-in-from-bottom duration-300 backdrop-blur">
          <div className="flex items-start gap-3.5">
            <div className="p-2 bg-amber-500/10 text-amber-500 rounded-xl border border-amber-500/20 shrink-0">
              <AlertTriangle size={18} className="animate-bounce" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-xs font-black text-amber-500 tracking-wider">ADMIN SYSTEM WARNING</h4>
              <p className="text-[11px] text-zinc-305 mt-1.5 leading-relaxed font-sans font-medium">
                {incident.message || 'Please maintain academic integrity while browsing.'}
              </p>
              <div className="flex justify-end mt-3.5">
                <button
                  onClick={() => setIncident(null)}
                  className="px-4 py-1.5 bg-zinc-950 hover:bg-amber-600 border border-zinc-820 hover:border-amber-500 text-zinc-400 hover:text-white transition-all rounded-lg text-[9px] font-black uppercase cursor-pointer"
                >
                  DISMISS ALERT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function WebsiteMetaUpdater() {
  const { homepageSettings } = useCourses();

  useEffect(() => {
    if (homepageSettings) {
      if (homepageSettings.tabTitle) {
        document.title = homepageSettings.tabTitle;
      }
      if (homepageSettings.faviconUrl) {
        const linkList = document.querySelectorAll("link[rel*='icon']");
        if (linkList.length > 0) {
          linkList.forEach(link => {
            (link as HTMLLinkElement).href = homepageSettings.faviconUrl!;
          });
        } else {
          const newLink = document.createElement('link');
          newLink.rel = 'icon';
          newLink.href = homepageSettings.faviconUrl;
          document.head.appendChild(newLink);
        }
      }

      // Dynamic Meta Description updating
      const descMeta = document.querySelector("meta[name='description']");
      const descText = homepageSettings.seoMetaDescription || 'EduCoreBD is a modern educational platform for students in Bangladesh, providing courses, online exams, and study guidelines.';
      if (descMeta) {
        descMeta.setAttribute('content', descText);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = descText;
        document.head.appendChild(meta);
      }

      // Dynamic Meta Keywords updating
      const keywordsMeta = document.querySelector("meta[name='keywords']");
      const keywordsText = homepageSettings.seoMetaKeywords || 'online school, educore, bangladesh education, hsc preparation, ssc preparation, online learning';
      if (keywordsMeta) {
        keywordsMeta.setAttribute('content', keywordsText);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'keywords';
        meta.content = keywordsText;
        document.head.appendChild(meta);
      }

      // Dynamic Google Site Verification Meta Tag updating
      if (homepageSettings.seoGoogleSearchConsoleMeta) {
        const gsvMeta = document.querySelector("meta[name='google-site-verification']");
        // Normalize: extract the content attribute if the user input contains a full meta tag, otherwise use raw string
        let gsvContent = homepageSettings.seoGoogleSearchConsoleMeta;
        if (gsvContent.includes('content=')) {
          const match = gsvContent.match(/content="([^"]*)"/i);
          if (match && match[1]) {
            gsvContent = match[1];
          }
        }
        gsvContent = gsvContent.replace('google-site-verification=', '').trim();

        if (gsvMeta) {
          gsvMeta.setAttribute('content', gsvContent);
        } else {
          const meta = document.createElement('meta');
          meta.name = 'google-site-verification';
          meta.content = gsvContent;
          document.head.appendChild(meta);
        }
      }

      // Dynamic Google Analytics integration for Client-side SPA
      if (homepageSettings.googleAnalyticsId) {
        const gaId = homepageSettings.googleAnalyticsId.trim();
        if (gaId.includes('<script')) {
          const existingGa = document.getElementById('educore-ga-script-element');
          if (!existingGa) {
            const container = document.createElement('div');
            container.id = 'educore-ga-script-element';
            container.innerHTML = gaId;
            Array.from(container.querySelectorAll('script')).forEach((oldScript) => {
              const newScript = document.createElement('script');
              Array.from(oldScript.attributes).forEach((attr) => {
                newScript.setAttribute(attr.name, attr.value);
              });
              newScript.appendChild(document.createTextNode(oldScript.innerHTML));
              document.head.appendChild(newScript);
            });
          }
        } else {
          const gaIdAttr = 'ga-script-' + gaId;
          const existingGa = document.getElementById(gaIdAttr);
          if (!existingGa) {
            // Script 1: gtag library load
            const script1 = document.createElement('script');
            script1.id = gaIdAttr;
            script1.async = true;
            script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
            document.head.appendChild(script1);

            // Script 2: gtag initialization
            const script2 = document.createElement('script');
            script2.id = gaIdAttr + '-init';
            script2.appendChild(document.createTextNode(`
              window.dataLayer = window.dataLayer || [];
              function gtag(){window.dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${gaId}', { page_path: window.location.pathname });
            `));
            document.head.appendChild(script2);
          }
        }
      }
    }
  }, [homepageSettings]);

  return null;
}

function RoutesContainer() {
  const { homepageSettings } = useCourses();
  const location = useLocation();

  const customPath = (homepageSettings?.adminCustomPath || 'ed-admin').trim().replace(/^\//, '');
  const cleanPath = location.pathname.substring(1).trim();

  // Check if current URL path matches custom admin login path exactly
  const isAdminPath = cleanPath === customPath;

  return (
    <Suspense fallback={
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    }>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/course/:id" element={<ProfileGuard><CourseDetail /></ProfileGuard>} />
        <Route path="/course/:courseId/content" element={<ProfileGuard><CourseContent /></ProfileGuard>} />
        <Route path="/course/:courseId/subject/:subjectId" element={<ProfileGuard><SubjectVideos /></ProfileGuard>} />
        <Route path="/course/:courseId/subject/:subjectId/chapter/:chapterId/lesson/:lessonId" element={<ProfileGuard><LessonView /></ProfileGuard>} />
        <Route path="/live" element={<ProfileGuard><LiveClasses /></ProfileGuard>} />
        <Route path="/dashboard" element={<ProfileGuard><Dashboard /></ProfileGuard>} />
        
        {/* Dynamic custom admin path - only accessible if it matches customPath exactly */}
        {customPath && (
          <Route path={`/${customPath}`} element={<AdminDashboard />} />
        )}

        <Route path="/free-classes" element={<FreeClasses />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/pages/:slug" element={<PageViewer />} />
        <Route path="/privacy" element={<PageViewer />} />
        <Route path="/refund" element={<PageViewer />} />
        <Route path="/about" element={<PageViewer />} />
        <Route path="/blog" element={<BlogsList />} />
        <Route path="/blog/:id" element={<BlogViewer />} />
        <Route path="/teacher-panel" element={<TeacherDashboard />} />
        
        {/* Dynamic catch-all for root-level custom pages (like /terms, etc.) */}
        <Route path="/:slug" element={<PageViewer />} />
        
        {/* Catch-all that falls back to admin if matching administration url, else returns simple message */}
        <Route path="*" element={isAdminPath ? <AdminDashboard /> : <p className="text-zinc-500 p-8 h-screen text-center">Page Not Found</p>} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CourseProvider>
          <Router>
            <PageTracker />
            <WebsiteMetaUpdater />
            <SecurityInterceptor>
              <div className="flex flex-col min-h-screen bg-zinc-950">
                <Navbar />
                <main className="flex-grow">
                  <RoutesContainer />
                </main>
                <Footer />
              </div>
            </SecurityInterceptor>
          </Router>
        </CourseProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
